import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import usersAPI from '@services/api/users'
import sessionsAPI from '@services/api/sessions'
import mfaAPI from '@/services/api/mfa'

import { CreateParams as RegisterParams } from '@services/api/users'
import { CreateParams as LoginParams } from '@services/api/sessions'
import { AuthenicateParams } from '@/services/api/mfa'

import { SESSION_TOKEN } from '@/utils/constants'
import { useUserStore } from '@/store/useUserStore'

interface AuthError {
  message: string
}

export const useAuth = () => {
  const setUser = useUserStore((state) => state.setUser)
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<AuthError | null>(null)

  const register = async (data: RegisterParams) => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await usersAPI.create(data)
      localStorage.setItem(SESSION_TOKEN, response.token)

      setUser(response.user)

      return response
    } catch (err: any) {
      setError({ message: t('auth.invalidCredentials') })
      setUser(null)

      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (data: LoginParams) => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await sessionsAPI.create(data)

      if (response.user && response.token) {
        localStorage.setItem(SESSION_TOKEN, response.token)
        setUser(response.user)
      }

      return response
    } catch (err: any) {
      setError({ message: t('auth.invalidCredentials') })
      setUser(null)

      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const authenticate = async (data: AuthenicateParams) => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await mfaAPI.authenticate(data)

      if (response.user && response.token) {
        localStorage.setItem(SESSION_TOKEN, response.token)
        setUser(response.user)
      } else {
        setError({ message: t('auth.invalidCredentials') })
        setUser(null)
      }

      return response
    } catch (err: any) {
      setError({ message: t('auth.invalidCredentials') })
      setUser(null)

      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      setIsLoading(true)
      setError(null)

      /*
        TODO: This should send request to destroy session key
        and remove user data from the store.
      */
      setUser(null)
      localStorage.removeItem(SESSION_TOKEN)
    } finally {
      setIsLoading(false)
    }
  }

  const clearError = () => {
    setError(null)
  }

  return {
    isLoading,
    error,
    register,
    login,
    authenticate,
    logout,
    clearError
  }
}

export default useAuth